import { useState, type FormEvent } from "react";
import type { AdminDictionaryItem, AdminDictionaryPatch } from "@omts/shared";
import { ActiveBadge, errMsg, useList } from "./common";

type Props = {
  /** Подпись во множественном числе: «Проекты». */
  title: string;
  placeholder: string;
  emptyText: string;
  load: () => Promise<AdminDictionaryItem[]>;
  create: (name: string) => Promise<AdminDictionaryItem>;
  update: (id: string, patch: AdminDictionaryPatch) => Promise<AdminDictionaryItem>;
};

/** Справочник «название + активен»: проекты и типы транспорта устроены одинаково. */
export function DictionaryTab({ title, placeholder, emptyText, load, create, update }: Props) {
  const { items, error, reload } = useList(load);
  const [name, setName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setAddError("Введите название");
    setBusy(true);
    setAddError(null);
    try {
      await create(name.trim());
      setName("");
      reload();
    } catch (err) {
      setAddError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id: string, p: AdminDictionaryPatch) => {
    setRowError(null);
    try {
      await update(id, p);
      setEditing(null);
      reload();
    } catch (err) {
      setRowError(errMsg(err));
    }
  };

  const rows = (items ?? []).filter((r) => showInactive || r.isActive);
  const inactiveCount = (items ?? []).filter((r) => !r.isActive).length;

  return (
    <>
      <form className="toolbar" onSubmit={(e) => void add(e)}>
        <div className="row">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} className={addError ? "invalid" : ""} />
          <button type="submit" className="btn primary" disabled={busy}>
            Добавить
          </button>
          {inactiveCount > 0 && (
            <label className="row hint" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              показывать неактивные ({inactiveCount})
            </label>
          )}
        </div>
        <span className="muted">{items ? `${title}: ${items.length}` : ""}</span>
      </form>
      {addError && <p className="error">{addError}</p>}
      {error && <p className="error">{error}</p>}
      {rowError && <p className="error">{rowError}</p>}
      <section className="card">
        {!items ? (
          <p className="muted">Загрузка…</p>
        ) : rows.length === 0 ? (
          <p className="muted">{emptyText}</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={r.isActive ? "" : "inactive"}>
                  <td>
                    {editing?.id === r.id ? (
                      <form
                        className="row"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (editing.name.trim()) void patch(r.id, { name: editing.name.trim() });
                        }}
                      >
                        <input value={editing.name} onChange={(e) => setEditing({ id: r.id, name: e.target.value })} autoFocus />
                        <button type="submit" className="btn small primary">
                          Сохранить
                        </button>
                        <button type="button" className="btn small" onClick={() => setEditing(null)}>
                          Отмена
                        </button>
                      </form>
                    ) : (
                      r.name
                    )}
                  </td>
                  <td>
                    <ActiveBadge active={r.isActive} />
                  </td>
                  <td>
                    <div className="actions-cell">
                      {editing?.id !== r.id && (
                        <button className="btn small" onClick={() => setEditing({ id: r.id, name: r.name })}>
                          Переименовать
                        </button>
                      )}
                      <button className="btn small" onClick={() => void patch(r.id, { isActive: !r.isActive })}>
                        {r.isActive ? "Деактивировать" : "Активировать"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <p className="hint">Неактивные записи не предлагаются в форме новой заявки, но в старых заявках остаются.</p>
    </>
  );
}
